import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { MdCheckCircle, MdPerson } from 'react-icons/md';

export interface TikTokSettingsValues {
  visibility: string;
  allowComment: boolean;
  allowDuet: boolean;
  allowStitch: boolean;
  discloseContent: boolean;
  brandType: string;
}

interface TikTokSettingsPanelProps {
  displayName?: string;
  username?: string;
  avatarUrl?: string;
  isVerified?: boolean;
  initialValues: TikTokSettingsValues;
  saving: boolean;
  onSave: (values: TikTokSettingsValues) => void;
}

const TikTokSettingsPanel: React.FC<TikTokSettingsPanelProps> = ({
  displayName,
  username,
  avatarUrl,
  isVerified,
  initialValues,
  saving,
  onSave,
}) => {
  const [visibility, setVisibility] = useState(initialValues.visibility);
  const [isPrivacyDropdownOpen, setIsPrivacyDropdownOpen] = useState(false);
  const privacyDropdownRef = useRef<HTMLDivElement>(null);
  const [allowComment, setAllowComment] = useState(initialValues.allowComment);
  const [allowDuet, setAllowDuet] = useState(initialValues.allowDuet);
  const [allowStitch, setAllowStitch] = useState(initialValues.allowStitch);
  const [discloseContent, setDiscloseContent] = useState(initialValues.discloseContent);
  const [brandType, setBrandType] = useState(initialValues.brandType);

  // Sync when initial values update (e.g. after fetching settings)
  useEffect(() => {
    setVisibility(initialValues.visibility);
    setAllowComment(initialValues.allowComment);
    setAllowDuet(initialValues.allowDuet);
    setAllowStitch(initialValues.allowStitch);
    setDiscloseContent(initialValues.discloseContent);
    setBrandType(initialValues.brandType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialValues.visibility,
    initialValues.allowComment,
    initialValues.allowDuet,
    initialValues.allowStitch,
    initialValues.discloseContent,
    initialValues.brandType,
  ]);

  // Close privacy dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (privacyDropdownRef.current && !privacyDropdownRef.current.contains(event.target as Node)) {
        setIsPrivacyDropdownOpen(false);
      }
    };

    if (isPrivacyDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPrivacyDropdownOpen]);

  const handleSave = () => {
    onSave({
      visibility,
      allowComment,
      allowDuet,
      allowStitch,
      discloseContent,
      brandType,
    });
  };

  return (
    <div className="max-w-7xl mx-auto bg-[#0f0f0f] rounded-xl p-10">
      <div className="grid grid-cols-2 gap-3 items-start">
        {/* Left Column - Compact profile card + post settings */}
        <div className="flex flex-col gap-3">
          {/* Compact Profile Card */}
          <div className=" rounded-2xl  flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                <MdPerson className="text-white" size={40} />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-white text-lg font-semibold truncate">{displayName}</h3>
                {isVerified && <MdCheckCircle className="text-blue-500 flex-shrink-0" size={18} />}
              </div>
              <p className="text-gray-400 text-sm truncate">@{username}</p>
            </div>
          </div>

          {/* Default Post Settings Card */}
          <div className=" mt-8">
            <h3 className="text-white text-lg font-medium mb-6">Default Post Settings</h3>

            {/* Custom Privacy Dropdown - NO default value, user MUST select */}
            <div className="relative mb-6" ref={privacyDropdownRef}>
              <label className="block text-sm font-normal mb-2 text-white">
                Who can view this video <span className="text-red-500">*</span>
              </label>

              <button
                type="button"
                onClick={() => setIsPrivacyDropdownOpen(!isPrivacyDropdownOpen)}
                className={`w-xs cursor-pointer bg-[#1e1e1e] border rounded-xl p-3 flex items-center justify-between transition-colors ${
                  visibility === '' ? 'border-red-500 text-gray-500' : 'border-gray-700 text-white'
                }`}
              >
                <span>{visibility || 'Select privacy level'}</span>
                <svg
                  className={`w-5 h-5 transition-transform ${isPrivacyDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isPrivacyDropdownOpen && (
                <div className="absolute z-10 w-xs mt-2 bg-[#1a1a1a] border border-gray-600 rounded-xl shadow-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setVisibility('Public');
                      setIsPrivacyDropdownOpen(false);
                    }}
                    className="w-full cursor-pointer px-4 py-3 text-left hover:bg-[#3a3a3a] transition-colors flex items-center gap-3"
                  >
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-medium text-white">Public</p>
                      <p className="text-xs text-gray-400">Everyone can watch your video</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setVisibility('Friends');
                      setIsPrivacyDropdownOpen(false);
                    }}
                    className="w-full cursor-pointer px-4 py-3 text-left hover:bg-[#3a3a3a] transition-colors flex items-center gap-3 border-t border-gray-700"
                  >
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    <div>
                      <p className="font-medium text-white">Friends</p>
                      <p className="text-xs text-gray-400">Only your friends can watch</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setVisibility('Private');
                      setIsPrivacyDropdownOpen(false);
                    }}
                    className="w-full cursor-pointer px-4 py-3 text-left hover:bg-[#3a3a3a] transition-colors flex items-center gap-3 border-t border-gray-700"
                  >
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <div>
                      <p className="font-medium text-white">Private</p>
                      <p className="text-xs text-gray-400">Only you can watch</p>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* Allow users to - All default to OFF (TikTok requirement) */}
            <div>
              <label className="block text-sm font-medium mb-3 text-white">Allow users to</label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allowComment}
                    onChange={(e) => setAllowComment(e.target.checked)}
                    className="w-5 h-5 rounded bg-[#1e1e1e] border-2 border-gray-700 checked:bg-orange-500 checked:border-orange-500 cursor-pointer"
                  />
                  <span className="text-sm text-white">Comment</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allowDuet}
                    onChange={(e) => setAllowDuet(e.target.checked)}
                    className="w-5 h-5 rounded bg-[#1e1e1e] border-2 border-gray-700 checked:bg-orange-500 checked:border-orange-500 cursor-pointer"
                  />
                  <span className="text-sm text-white">Duet</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allowStitch}
                    onChange={(e) => setAllowStitch(e.target.checked)}
                    className="w-5 h-5 rounded bg-[#1e1e1e] border-2 border-gray-700 checked:bg-orange-500 checked:border-orange-500 cursor-pointer"
                  />
                  <span className="text-sm text-white">Stitch</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">All interaction controls are disabled by default. Enable as needed.</p>
            </div>
          </div>
        </div>

        {/* Right Column - Disclose video content */}
        <div className="">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold mb-1 text-white text-lg">Disclose video content</h3>
              <p className="text-xs text-gray-400">
                Turn on to disclose that this video promotes goods or services in exchange for something of value.
              </p>
            </div>
            <button
              onClick={() => setDiscloseContent(!discloseContent)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
                discloseContent ? 'bg-orange-500' : 'bg-gray-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  discloseContent ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {discloseContent && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-3 mt-4"
            >
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3">
                <p className="text-xs text-blue-300">
                  Your video will be labeled "Promotional content". This cannot be changed once your video is posted.
                </p>
              </div>

              <p className="text-sm text-gray-400">
                Turn on to disclose that this video promotes goods or services in exchange for something of value. Your video could promote yourself, a third party, or both.
              </p>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-[#1e1e1e] transition-colors">
                <input
                  type="radio"
                  name="brandType"
                  value="your-brand"
                  checked={brandType === 'your-brand'}
                  onChange={(e) => setBrandType(e.target.value)}
                  className="mt-1 w-5 h-5 text-orange-500"
                />
                <div>
                  <p className="font-medium mb-1 text-white">Your brand</p>
                  <p className="text-xs text-gray-400">
                    You are promoting yourself or your own business. This video will be classified as Brand Organic.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-[#1e1e1e] transition-colors">
                <input
                  type="radio"
                  name="brandType"
                  value="branded-content"
                  checked={brandType === 'branded-content'}
                  onChange={(e) => setBrandType(e.target.value)}
                  className="mt-1 w-5 h-5 text-orange-500"
                />
                <div>
                  <p className="font-medium mb-1 text-white">Branded content</p>
                  <p className="text-xs text-gray-400">
                    You are promoting another brand or a third party. This video will be classified as Branded Content.
                  </p>
                </div>
              </label>
            </motion.div>
          )}

          {/* Music Usage Consent - MUST SHOW ALWAYS BEFORE UPLOAD (TikTok requirement) */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mt-6">
            <p className="text-sm text-yellow-200">
              By posting, you agree to TikTok's{' '}
              <a
                href="https://www.tiktok.com/legal/music-usage-confirmation"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-400 hover:underline font-medium"
              >
                Music Usage Confirmation
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Save Button - centered below both columns */}
      <div className="flex justify-center mt-8">
        <button
          type="button"
          onClick={handleSave}
          disabled={!visibility || saving}
          className="px-16 py-3 rounded-full font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-brand-primary hover:bg-[#ee4423] text-white shadow-lg cursor-pointer"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default TikTokSettingsPanel;
